import { html } from 'neverland';

// Password complexity rules — these MUST mirror the Cognito user pool
// password_policy in infrastructure/core/main.tf (minimum_length = 8,
// require_uppercase/lowercase/numbers/symbols = true). Keeping them in one
// place means the live checklist shown to users matches exactly what the
// server will accept.
export const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'An uppercase letter (A-Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'A lowercase letter (a-z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'A number (0-9)', test: (pw) => /[0-9]/.test(pw) },
  // Cognito's symbol set: = + - ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~ ` and space
  { id: 'symbol', label: 'A special character (e.g. ! @ # $ %)', test: (pw) => /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+= -]/.test(pw) },
];

// True only when every rule passes — use to gate the submit button.
export function passwordMeetsAllRules(pw) {
  return PASSWORD_RULES.every((r) => r.test(pw || ''));
}

// Returns the labels of the rules a password fails (handy for error messages).
export function unmetPasswordRules(pw) {
  return PASSWORD_RULES.filter((r) => !r.test(pw || '')).map((r) => r.label);
}

// Live checklist UI. Pass the current password value and (optionally) the
// confirm value; each rule shows a tick when satisfied and a cross otherwise.
// Before the user types anything (pw === ''), rules render in a neutral state
// so we don't greet them with a wall of red crosses.
export function PasswordChecklist(pw, confirmPw) {
  const touched = (pw || '').length > 0;

  const ruleRow = (satisfied, label) => {
    const state = !touched ? 'pending' : satisfied ? 'met' : 'unmet';
    const icon = !touched ? 'fa-circle' : satisfied ? 'fa-check-circle' : 'fa-times-circle';
    return html`
      <li class="pw-rule pw-rule-${state}">
        <i class="fal ${icon}"></i>&ensp;${label}
      </li>
    `;
  };

  const rows = PASSWORD_RULES.map((r) => ruleRow(r.test(pw || ''), r.label));

  // Only show the "passwords match" row once a confirm value has been entered.
  const showMatch = (confirmPw || '').length > 0;
  const matchRow = showMatch
    ? ruleRow((pw || '') === confirmPw, 'Passwords match')
    : '';

  return html`
    <ul class="pw-checklist" aria-label="Password requirements">
      ${rows}
      ${matchRow}
    </ul>
  `;
}
